import { MessageInitShape } from '@bufbuild/protobuf';

import {
  FeatureKind,
  Folder,
  GetCollectionResponse,
  Line,
  Point,
  Polygon,
  WriteSchema,
} from 'trails_lat/proto/data_pb';

import { DEFAULT_ICON } from '../workers/collection_schema';
import { getIconImageId } from './images';
import { SavedSchema } from './schema';

// The jsonb every feature carries. Colors are #rrggbb.
export interface FeatureData {
  name?: string;
  description?: string;
  folder_id?: string;
  icon?: string;
  fill?: string;
  stroke?: string;
  width_px?: number;
  // Set on a line drawn with the marker, which is the zoom it was drawn at. width_px is how wide
  // the stroke is at that zoom, and it scales with the map.
  marker_zoom?: number;
  [key: string]: boolean|number|string|undefined;
}

interface FeatureBase {
  id: string;
  // The collection version this feature was last saved at (or 0 if unsaved)
  version: bigint;
  data: FeatureData;
}

export interface EditableFolder extends FeatureBase {
  kind: 'folder';
}

// Geometry arrays are replaced rather than written through, because undo holds on to them.
export interface EditableLine extends FeatureBase {
  kind: 'line';
  latLngE7: Int32Array;
  elevationCentimeters: Int32Array|undefined;
  timeSeconds: BigInt64Array|undefined;
}

export interface EditablePoint extends FeatureBase {
  kind: 'point';
  latE7: number;
  lngE7: number;
  elevationCentimeters: number|undefined;
}

// One ring without its first vertex repeated at the end, see data.proto#Polygon.
export interface EditablePolygon extends FeatureBase {
  kind: 'polygon';
  latLngE7: Int32Array;
}

export type EditableFeature = EditableFolder|EditableLine|EditablePoint|EditablePolygon;

// The features with a vertex array, which the pointer tool edits the same way
export type EditableShape = EditableLine|EditablePolygon;

export function isShape(feature: EditableFeature|undefined): feature is EditableShape {
  return feature?.kind === 'line' || feature?.kind === 'polygon';
}

export type SaveOp = 'delete'|'put';

/**
 * Returns the glyph or image icon a point is drawn as. Plain ASCII is a symbol name from whatever
 * tool wrote the file, like "point", rather than something to draw, so it gets the default too.
 */
export function pointIcon(icon: string|undefined): string {
  if (icon && getIconImageId(icon) !== undefined) {
    return icon;
  }
  return importableIcon(icon) ?? DEFAULT_ICON;
}

/** Returns the icon worth keeping from an imported symbol, see pointIcon. */
export function importableIcon(icon: string|undefined): string|undefined {
  return icon && !/^[\x00-\x7F]*$/.test(icon) ? icon : undefined;
}

/**
 * Maps each feature's id to its folder, or undefined for the root. A folder_id naming a missing
 * folder counts as the root, or else the feature would vanish from the list with nothing to open
 * to find it. Folders whose folder_ids form a cycle, which an import or two tabs dragging at once
 * can make, are unreachable from the root, so we hang the first one we find at the root.
 */
export function getParents(features: Iterable<EditableFeature>): Map<string, string|undefined> {
  const byId = new Map<string, EditableFeature>();
  for (const feature of features) {
    byId.set(feature.id, feature);
  }
  const children = new Map<string|undefined, EditableFeature[]>();
  for (const feature of byId.values()) {
    const folder = feature.data.folder_id;
    const parent =
        folder !== undefined && byId.get(folder)?.kind === 'folder' ? folder : undefined;
    const siblings = children.get(parent) ?? [];
    siblings.push(feature);
    children.set(parent, siblings);
  }

  const parents = new Map<string, string|undefined>();
  const visit = (parent: string|undefined) => {
    for (const child of children.get(parent) ?? []) {
      if (!parents.has(child.id)) {
        parents.set(child.id, parent);
        visit(child.id);
      }
    }
  };
  visit(undefined);
  for (const id of byId.keys()) {
    if (!parents.has(id)) {
      parents.set(id, undefined);
      visit(id);
    }
  }
  return parents;
}

/** Copies a feature so that later edits to the original leave the copy alone. */
export function snapshot<F extends EditableFeature>(feature: F): F {
  return {...feature, data: {...feature.data}};
}

export function featuresFromProto(response: GetCollectionResponse|undefined): EditableFeature[] {
  return [
    ...(response?.folders ?? []).map(folderFromProto),
    ...(response?.lines ?? []).map(lineFromProto),
    ...(response?.points ?? []).map(pointFromProto),
    ...(response?.polygons ?? []).map(polygonFromProto),
  ];
}

function folderFromProto(folder: Folder): EditableFolder {
  return {
    kind: 'folder',
    id: folder.id,
    version: folder.version,
    data: parseData(folder.data),
  };
}

function lineFromProto(line: Line): EditableLine {
  return {
    kind: 'line',
    id: line.id,
    version: line.version,
    data: parseData(line.data),
    latLngE7: Int32Array.from(line.latLngE7),
    elevationCentimeters:
        line.elevationCentimeters.length > 0
            ? Int32Array.from(line.elevationCentimeters)
            : undefined,
    timeSeconds: line.timeSeconds.length > 0 ? BigInt64Array.from(line.timeSeconds) : undefined,
  };
}

function pointFromProto(point: Point): EditablePoint {
  return {
    kind: 'point',
    id: point.id,
    version: point.version,
    data: parseData(point.data),
    latE7: point.latE7,
    lngE7: point.lngE7,
    elevationCentimeters: point.elevationCentimeters,
  };
}

function polygonFromProto(polygon: Polygon): EditablePolygon {
  return {
    kind: 'polygon',
    id: polygon.id,
    version: polygon.version,
    data: parseData(polygon.data),
    latLngE7: Int32Array.from(polygon.latLngE7),
  };
}

export function toWrite(
    op: SaveOp, feature: EditableFeature): MessageInitShape<typeof WriteSchema> {
  if (op === 'delete') {
    return {
      write: {
        case: 'delete',
        value: {
          kind: FEATURE_KINDS[feature.kind],
          id: feature.id,
          baseVersion: feature.version,
        },
      },
    };
  }

  const data = JSON.stringify(feature.data);
  if (feature.kind === 'folder') {
    return {
      write: {
        case: 'putFolder',
        value: {id: feature.id, data, version: feature.version},
      },
    };
  } else if (feature.kind === 'line') {
    return {
      write: {
        case: 'putLine',
        value: {
          id: feature.id,
          data,
          latLngE7: Array.from(feature.latLngE7),
          elevationCentimeters:
              feature.elevationCentimeters ? Array.from(feature.elevationCentimeters) : [],
          timeSeconds: feature.timeSeconds ? Array.from(feature.timeSeconds) : [],
          version: feature.version,
        },
      },
    };
  } else if (feature.kind === 'point') {
    return {
      write: {
        case: 'putPoint',
        value: {
          id: feature.id,
          data,
          latE7: feature.latE7,
          lngE7: feature.lngE7,
          elevationCentimeters: feature.elevationCentimeters,
          version: feature.version,
        },
      },
    };
  } else {
    return {
      write: {
        case: 'putPolygon',
        value: {
          id: feature.id,
          data,
          latLngE7: Array.from(feature.latLngE7),
          version: feature.version,
        },
      },
    };
  }
}

export function schemaToWrite(saved: SavedSchema): MessageInitShape<typeof WriteSchema> {
  return {
    write: {
      case: 'putSchema',
      value: {schema: JSON.stringify(saved.schema), version: saved.version},
    },
  };
}

const FEATURE_KINDS = {
  folder: FeatureKind.FOLDER,
  line: FeatureKind.LINE,
  point: FeatureKind.POINT,
  polygon: FeatureKind.POLYGON,
} as const;

function parseData(data: string): FeatureData {
  return data ? JSON.parse(data) : {};
}
